
import i18n from '@/locales/init'; 

export interface IAnalyzeGeneratorProps {
  display_answer: string;
  ai_analyze: string;
  is_reading?: boolean;

  // 阅读
  key_locating_words?: Array<{
    locating_word: string;
    syn_ary: Array<{
      synonym: string;
      target_sen: any[];
    }>;
  }>;
  central_sentences?: Array<{
    target_para: number | string | null;
    index: number;
    length: number;
    sentence: string; // 英文
    translation: string;
  }>;

  // 听力
  audio?: {
    start_time: number;
    end_time: number;
  };

}

export interface Location {
  index: number;
  length: number;
  target_para: any[] | boolean | number | number | { [key: string]: any; } | null | string;
}

export interface Analyze {
  content: string;
  location: Location[] | { start_time: number; end_time: number; } | null;
  type: string;
}

// 换行
const getBreakNode = () => {
  return {
    type: 'break',
    content: '',
    location: null,
  };
};

const getReadingSource = (q: IAnalyzeGeneratorProps): Analyze[] => {
  if (!q.is_reading) return [];

  const paragraphLabel = (target: unknown): string | null => {
    const value = typeof target === 'string' ? target.trim() : target;
    if (typeof value === 'number' || (typeof value === 'string' && /^\d+$/.test(value))) {
      const index = Number(value);
      return Number.isSafeInteger(index) && index >= 0 && Number.isSafeInteger(index + 1)
        ? i18n.t('common.reading_source_numbered_paragraph', { v: index + 1 }) : null;
    }
    if (typeof value === 'string' && /^[a-z]$/i.test(value)) {
      return i18n.t('common.reading_source_lettered_paragraph', { v: value.toUpperCase() });
    }
    return typeof value === 'string' && value.toLowerCase() === 'pre-a'
      ? i18n.t('common.reading_source_introduction') : null;
  };

  let labels = (q.central_sentences || []).map(item => paragraphLabel(item.target_para)).filter(Boolean);
  if (!labels.length) {
    labels = (q.key_locating_words || []).flatMap(item => item.syn_ary || [])
      .flatMap(item => item.target_sen || []).map(item => paragraphLabel(item.target_para)).filter(Boolean);
  }
  if (!labels.length) return [];

  return [{
    type: 'text',
    content: i18n.t('common.reading_source', {
      paragraphs: Array.from(new Set(labels)).join(i18n.t('common.reading_source_separator')),
    }),
    location: null,
  }, getBreakNode()];
};

// 核心词
const getKeyWord = (items: IAnalyzeGeneratorProps['key_locating_words']): Analyze[] => {
  const result: Analyze[] = [];
  if (!items || items.length === 0) {
    return result;
  }
  result.push({
    type: 'header',
    content: `${i18n.t('common.dauyan_core_words')}:`,
    location: null,
  }, getBreakNode(), {
    type: 'text',
    content: items.map((item) => item.locating_word).join('; '),
    location: null,
  }, getBreakNode());

  return result;
};

// 文中同义替换
const getKeywordsList = (items: IAnalyzeGeneratorProps['key_locating_words']): Analyze[] => {
  const result: Analyze[] = [];

  if (!items || items.length === 0) {
    return result;
  }

  result.push(
    {
      type: 'header',
      content: `${i18n.t('common.dauyan_essay_same_replace')}:`,
      location: null,
    }, getBreakNode()
  );

  items.forEach((item, itemIndex) => {
    result.push({
      // eslint-disable-next-line no-irregular-whitespace
      content: i18n.t('common.dauyan_question_value_essay', {v: item.locating_word}),
      type: 'text',
      location: null,
    });

    item.syn_ary.forEach((syn, synIndex) => {
      if (synIndex !== 0) {
        result.push({
          type: 'text',
          content: ' & ',
          location: null,
        });
      }
      result.push({
        type: 'reference',
        content: syn.synonym,
        location: syn.target_sen.map((ele: any) => {
          return {
            ...ele,
            target_sen: '',
          };
        }),
      });
    });

    result.push(getBreakNode());
  });
  return result;
};

// 原文及翻译
const getTranslateList = (items: IAnalyzeGeneratorProps['central_sentences']): Analyze[] => {
  const result: Analyze[] = [];
  if (!items || items.length === 0) {
    return result;
  }

  result.push({
    type: 'header',
    content: `${i18n.t('common.dauyan_origin_essay_and_translate')}:`,
    location: null,
  }, getBreakNode());

  items.forEach((item, index) => {
    result.push({
      type: 'reference',
      content: item.sentence,
      location: [{
        index: item.index,
        length: item.length,
        target_para: item.target_para,
      }],
    }, getBreakNode(), {
      type: 'text',
      content: item.translation,
      location: null,
    });
  });

  return result;
};

// ai解析
const getAIAnalyze = (ai_analyze: string) => {
  const result = [{
    type: 'header',
    content: `${i18n.t('common.dauyan_question_analysis')}:`,
    location: null,
  }, getBreakNode()];

  const list = ai_analyze.split('\n');

  list.forEach((s, index) => {
    if (!s) {
      return;
    }
    result.push({
      content: s,
      type: 'text',
      location: null,
    });
    if (index < list.length - 1) {
      result.push(getBreakNode());
    }
  });

  return result;
};

const getAudioList = (audio: IAnalyzeGeneratorProps['audio']): Analyze[] => {
  if (!audio) {
    return [];
  }
  return [{
    type: 'header',
    content: `${i18n.t('common.dauyan_audio_source')}:`,
    location: null,
  }, getBreakNode(), {
    type: 'audio',
    content: '',
    location: {
      start_time: audio.start_time,
      end_time: audio.end_time,
    },
  }, getBreakNode()
  ];
};


export const analyzeGenerator = (q: IAnalyzeGeneratorProps) => {
  const result: Analyze[] = [];
  console.log(q.display_answer);
  // 答案
  result.push({
    type: 'answer',
    content: i18n.t('common.dauyan_question_answer_v') + q.display_answer,
    location: null,
  }, getBreakNode());

  result.push(...getReadingSource(q));

  // 音频
  result.push(...getAudioList(q.audio));
  // 核心词
  result.push(...getKeyWord(q.key_locating_words));
  // 文中同义替换
  result.push(...getKeywordsList(q.key_locating_words));
  // 原文翻译
  result.push(...getTranslateList(q.central_sentences));
  // 解析
  result.push(...getAIAnalyze(q.ai_analyze));


  return result;
};
